package kpt.core.database.crypto.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow
import kpt.core.database.crypto.entity.CoinDetailEntity

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "OPT_IN_USAGE_ERROR", "OPT_IN_USAGE", "MemberExtensionConflict", "CAN_BE_VAL"])
internal class CoinDetailDao_Impl(
  __db: RoomDatabase,
) : CoinDetailDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfCoinDetailEntity: EntityUpsertAdapter<CoinDetailEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfCoinDetailEntity = EntityUpsertAdapter<CoinDetailEntity>(object : EntityInsertAdapter<CoinDetailEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `coin_detail` (`id`,`name`,`symbol`,`imageUrl`,`currentPrice`,`marketCap`,`marketCapRank`,`priceChangePercent24h`,`high24h`,`low24h`,`circulatingSupply`,`maxSupply`,`description`,`fetchedAt`) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: CoinDetailEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        statement.bindText(3, entity.symbol)
        statement.bindText(4, entity.imageUrl)
        statement.bindDouble(5, entity.currentPrice)
        statement.bindLong(6, entity.marketCap)
        statement.bindLong(7, entity.marketCapRank.toLong())
        statement.bindDouble(8, entity.priceChangePercent24h)
        statement.bindDouble(9, entity.high24h)
        statement.bindDouble(10, entity.low24h)
        statement.bindDouble(11, entity.circulatingSupply)
        val _tmpMaxSupply: Double? = entity.maxSupply
        if (_tmpMaxSupply == null) {
          statement.bindNull(12)
        } else {
          statement.bindDouble(12, _tmpMaxSupply)
        }
        statement.bindText(13, entity.description)
        statement.bindLong(14, entity.fetchedAt)
      }
    }, object : EntityDeleteOrUpdateAdapter<CoinDetailEntity>() {
      protected override fun createQuery(): String = "UPDATE `coin_detail` SET `id` = ?,`name` = ?,`symbol` = ?,`imageUrl` = ?,`currentPrice` = ?,`marketCap` = ?,`marketCapRank` = ?,`priceChangePercent24h` = ?,`high24h` = ?,`low24h` = ?,`circulatingSupply` = ?,`maxSupply` = ?,`description` = ?,`fetchedAt` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: CoinDetailEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        statement.bindText(3, entity.symbol)
        statement.bindText(4, entity.imageUrl)
        statement.bindDouble(5, entity.currentPrice)
        statement.bindLong(6, entity.marketCap)
        statement.bindLong(7, entity.marketCapRank.toLong())
        statement.bindDouble(8, entity.priceChangePercent24h)
        statement.bindDouble(9, entity.high24h)
        statement.bindDouble(10, entity.low24h)
        statement.bindDouble(11, entity.circulatingSupply)
        val _tmpMaxSupply: Double? = entity.maxSupply
        if (_tmpMaxSupply == null) {
          statement.bindNull(12)
        } else {
          statement.bindDouble(12, _tmpMaxSupply)
        }
        statement.bindText(13, entity.description)
        statement.bindLong(14, entity.fetchedAt)
        statement.bindText(15, entity.id)
      }
    })
  }

  public override suspend fun upsert(entity: CoinDetailEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfCoinDetailEntity.upsert(_connection, entity)
  }

  public override fun getById(coinId: String): Flow<CoinDetailEntity?> {
    val _sql: String = "SELECT * FROM coin_detail WHERE id = ? LIMIT 1"
    return createFlow(__db, false, arrayOf("coin_detail")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, coinId)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfSymbol: Int = getColumnIndexOrThrow(_stmt, "symbol")
        val _columnIndexOfImageUrl: Int = getColumnIndexOrThrow(_stmt, "imageUrl")
        val _columnIndexOfCurrentPrice: Int = getColumnIndexOrThrow(_stmt, "currentPrice")
        val _columnIndexOfMarketCap: Int = getColumnIndexOrThrow(_stmt, "marketCap")
        val _columnIndexOfMarketCapRank: Int = getColumnIndexOrThrow(_stmt, "marketCapRank")
        val _columnIndexOfPriceChangePercent24h: Int = getColumnIndexOrThrow(_stmt, "priceChangePercent24h")
        val _columnIndexOfHigh24h: Int = getColumnIndexOrThrow(_stmt, "high24h")
        val _columnIndexOfLow24h: Int = getColumnIndexOrThrow(_stmt, "low24h")
        val _columnIndexOfCirculatingSupply: Int = getColumnIndexOrThrow(_stmt, "circulatingSupply")
        val _columnIndexOfMaxSupply: Int = getColumnIndexOrThrow(_stmt, "maxSupply")
        val _columnIndexOfDescription: Int = getColumnIndexOrThrow(_stmt, "description")
        val _columnIndexOfFetchedAt: Int = getColumnIndexOrThrow(_stmt, "fetchedAt")
        val _result: CoinDetailEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpSymbol: String
          _tmpSymbol = _stmt.getText(_columnIndexOfSymbol)
          val _tmpImageUrl: String
          _tmpImageUrl = _stmt.getText(_columnIndexOfImageUrl)
          val _tmpCurrentPrice: Double
          _tmpCurrentPrice = _stmt.getDouble(_columnIndexOfCurrentPrice)
          val _tmpMarketCap: Long
          _tmpMarketCap = _stmt.getLong(_columnIndexOfMarketCap)
          val _tmpMarketCapRank: Int
          _tmpMarketCapRank = _stmt.getLong(_columnIndexOfMarketCapRank).toInt()
          val _tmpPriceChangePercent24h: Double
          _tmpPriceChangePercent24h = _stmt.getDouble(_columnIndexOfPriceChangePercent24h)
          val _tmpHigh24h: Double
          _tmpHigh24h = _stmt.getDouble(_columnIndexOfHigh24h)
          val _tmpLow24h: Double
          _tmpLow24h = _stmt.getDouble(_columnIndexOfLow24h)
          val _tmpCirculatingSupply: Double
          _tmpCirculatingSupply = _stmt.getDouble(_columnIndexOfCirculatingSupply)
          val _tmpMaxSupply: Double?
          if (_stmt.isNull(_columnIndexOfMaxSupply)) {
            _tmpMaxSupply = null
          } else {
            _tmpMaxSupply = _stmt.getDouble(_columnIndexOfMaxSupply)
          }
          val _tmpDescription: String
          _tmpDescription = _stmt.getText(_columnIndexOfDescription)
          val _tmpFetchedAt: Long
          _tmpFetchedAt = _stmt.getLong(_columnIndexOfFetchedAt)
          _result = CoinDetailEntity(_tmpId,_tmpName,_tmpSymbol,_tmpImageUrl,_tmpCurrentPrice,_tmpMarketCap,_tmpMarketCapRank,_tmpPriceChangePercent24h,_tmpHigh24h,_tmpLow24h,_tmpCirculatingSupply,_tmpMaxSupply,_tmpDescription,_tmpFetchedAt)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete(coinId: String) {
    val _sql: String = "DELETE FROM coin_detail WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, coinId)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM coin_detail"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
